import React, { useMemo } from "react"
import ReactApexChart from "react-apexcharts"

const COLORS = ["#4361ee", "#22c55e", "#f59e0b", "#a855f7", "#ef4444"]

const TopCustomersPieChart = ({ data = [] }) => {
  const top5 = useMemo(
    () =>
      [...data]
        .sort(
          (a, b) =>
            Number(b.totalWeeklySalesValue) -
            Number(a.totalWeeklySalesValue)
        )
        .slice(0, 5),
    [data]
  )

  const total = data.reduce(
    (s, i) => s + Number(i.totalWeeklySalesValue),
    0
  )
  const top5Total = top5.reduce(
    (s, i) => s + Number(i.totalWeeklySalesValue),
    0
  )

  const values = top5.map(i => Number(i.totalWeeklySalesValue))
  const labels = top5.map(i => i.customerName)
  const percents = top5.map(i =>
    ((Number(i.totalWeeklySalesValue) / total) * 100).toFixed(0)
  )

  const options = {
    chart: {
      type: "pie", // ✅ changed
      toolbar: { show: false },
      background: "white",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 900,
      },
    },
    colors: COLORS,
    labels,
    stroke: {
      colors: ["#fff"],
      width: 2,
    },
    fill: {
      opacity: 1, // ✅ changed from 0.88
    },
    dataLabels: {
      enabled: true,
      formatter: (val, { seriesIndex }) => `${percents[seriesIndex]}%`,
      style: {
        fontSize: "12px",
        fontWeight: 700,
        colors: ["#fff"],
      },
      dropShadow: { enabled: false },
    },
    // ✅ removed plotOptions.polarArea
    // ✅ removed yaxis
    tooltip: {
      theme: "light",
      y: {
        formatter: val => `£ ${Math.round(val).toLocaleString("en-GB")}`,
      },
    },
    legend: { show: false },
  }

  return (
    <div
      style={{
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
      className="bg-light"
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h6
            className="mb-1 fw-bold"
            style={{ color: "#1a1a2e", fontSize: "16px" }}
          >
            Top 5 Customers
          </h6>
          <small style={{ color: "#444", fontSize: "14px" }}>
            Revenue contribution breakdown
          </small>
        </div>
        <div
          style={{
            background: "#eef0fb",
            color: "#4361ee",
            borderRadius: "8px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          Top 5: £ {Math.round(top5Total).toLocaleString("en-GB")}
        </div>
      </div>

      {/* Chart + Legend side by side */}
      <div className="d-flex align-items-center gap-5">
        {/* ✅ Pie Chart */}
        <div style={{ flex: "0 0 auto" }}>
          <ReactApexChart
            options={options}
            series={values}
            type="pie" // ✅ changed
            height={350}
            width={350}
          />
        </div>

        {/* Custom Legend */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {top5.map((customer, i) => {
            const pct = (
              (Math.round(customer.totalWeeklySalesValue) / total) *
              100
            ).toFixed(1)
            return (
              <div key={i}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: COLORS[i],
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#1a1a2e",
                      }}
                    >
                      {customer.customerName}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#444",
                        fontWeight: 500,
                      }}
                    >
                      {new Intl.NumberFormat("en-GB", {
                        style: "currency",
                        currency: "GBP",
                        minimumFractionDigits: 0,
                      }).format(Math.round(customer.totalWeeklySalesValue))}
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: COLORS[i],
                        minWidth: "36px",
                        textAlign: "right",
                      }}
                    >
                      {pct}%
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div
                  style={{
                    height: "5px",
                    background: "#999",
                    borderRadius: "99px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${pct}%`,
                      background: COLORS[i],
                      borderRadius: "99px",
                      transition: "width 0.8s ease",
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default TopCustomersPieChart
